import React, { useState } from 'react';
import { Box, Typography, Chip, Paper, Stack, Divider, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { PatientBoardConfig, PatientCardVM, phaseStyle } from './types';
import { getScoreStyles } from './scoreStyles';

const CHIP_SX = { fontWeight: 600, fontSize: '0.68rem', height: 20 };

const DetailPanel: React.FC<{ patient: PatientCardVM; config: PatientBoardConfig }> = ({ patient, config }) => {
  const navigate = useNavigate();
  const phStyle = phaseStyle(config, patient.phaseKey);
  const scoreStyle = getScoreStyles(patient.scoreCell.score);

  return (
    <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
        <Box>
          <Typography variant="h6" fontWeight={700}>{patient.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            Age {patient.age} · DOB {patient.dob} · {patient.locationSecondary} {patient.locationPrimary}
          </Typography>
          <Stack direction="row" spacing={0.75} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
            <Chip label={phStyle.label} size="small" sx={{ ...CHIP_SX, bgcolor: phStyle.bgcolor, color: phStyle.color }} />
            {patient.urgency && (
              <Chip label={patient.urgency.label} size="small" sx={{ ...CHIP_SX, bgcolor: patient.urgency.bgcolor, color: patient.urgency.color }} />
            )}
            <Chip
              label={`${patient.scoreCell.label} · ${patient.scoreCell.score}`}
              size="small"
              sx={{ ...CHIP_SX, ...scoreStyle }}
            />
          </Stack>
        </Box>
        <Button
          size="small"
          variant="outlined"
          endIcon={<OpenInNewIcon sx={{ fontSize: '0.8rem' }} />}
          onClick={() => navigate(`${config.routeBase}/patient/${patient.id}`)}
          sx={{ flexShrink: 0, ml: 2, fontSize: '0.75rem' }}
        >
          Full Detail
        </Button>
      </Stack>

      <Divider />

      <Stack spacing={1.25}>
        {patient.admitDate && (
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">Admit</Typography>
            <Typography variant="caption">{patient.admitDate}</Typography>
          </Stack>
        )}
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">Expected Discharge</Typography>
          <Typography variant="caption">{patient.expectedDischarge}</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">Current LOS</Typography>
          <Typography variant="caption">
            {patient.currentLOS}d{patient.losTargetDays != null ? ` (target ${patient.losTargetDays}d)` : ''}
          </Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">Attending</Typography>
          <Typography variant="caption">{patient.attending ?? patient.physician}</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">Primary Diagnosis</Typography>
          <Typography variant="caption">{patient.diagnosis}</Typography>
        </Stack>
        {patient.insuranceLine && (
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">Insurance</Typography>
            <Typography variant="caption">{patient.insuranceLine}</Typography>
          </Stack>
        )}
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">Assigned</Typography>
          <Typography variant="caption">{patient.assignedToName ?? 'Unassigned'}</Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

const ListItem: React.FC<{ patient: PatientCardVM; config: PatientBoardConfig; selected: boolean; onClick: () => void }> = ({
  patient, config, selected, onClick,
}) => {
  const s = phaseStyle(config, patient.phaseKey);
  return (
    <Box
      onClick={onClick}
      sx={{
        px: 2, py: 1.25,
        cursor: 'pointer',
        borderBottom: '1px solid',
        borderColor: 'divider',
        borderLeft: '3px solid',
        borderLeftColor: selected ? 'primary.main' : (patient.urgency?.borderColor ?? 'transparent'),
        bgcolor: selected ? 'primary.50' : 'transparent',
        '&:hover': { bgcolor: selected ? 'primary.50' : 'action.hover' },
        transition: 'background-color 0.1s',
      }}
    >
      <Typography variant="body2" fontWeight={selected ? 600 : 500} noWrap>{patient.name}</Typography>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.25 }}>
        <Chip label={s.label} size="small" sx={{ bgcolor: s.bgcolor, color: s.color, fontWeight: 600, fontSize: '0.65rem', height: 18 }} />
        <Typography variant="caption" color="text.disabled">{patient.locationSecondary}</Typography>
      </Stack>
    </Box>
  );
};

interface Props {
  patients: PatientCardVM[];
  config: PatientBoardConfig;
}

const PatientBoardSplitView: React.FC<Props> = ({ patients, config }) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = patients.find(p => p.id === selectedId) ?? patients[0] ?? null;

  return (
    <Box sx={{ display: 'flex', gap: 2, minHeight: 0, flex: 1 }}>
      <Paper variant="outlined" sx={{ flex: '0 0 280px', overflowY: 'auto', borderRadius: 1 }}>
        {patients.length === 0 ? (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', p: 2 }}>
            No patients
          </Typography>
        ) : (
          patients.map(p => (
            <ListItem
              key={p.id}
              patient={p}
              config={config}
              selected={p.id === selected?.id}
              onClick={() => setSelectedId(p.id)}
            />
          ))
        )}
      </Paper>

      <Paper variant="outlined" sx={{ flex: 1, overflowY: 'auto', borderRadius: 1 }}>
        {selected ? (
          <DetailPanel patient={selected} config={config} />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography variant="body2" color="text.disabled">Select a patient to begin</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default PatientBoardSplitView;
